import type { Meter } from "@/models/meter"
import type { Organization } from "@/models/organization"
import type { Site } from "@/models/site"
import type { BillTextFilterQueryOptions } from "@/services/core-api/types"

interface BillsExportQuery {
  format?: "csv" | "xlsx"
  meterId?: Meter["id"]
  /**
   * Core's published comma-separated meter id list (e.g. "1,2,3"), as
   * resolved from the meter-code filter panel. Takes precedence over
   * `meterId`, and -- unlike `meterId` -- is sent together with `siteId`,
   * since the meter-code filter still names the site as a whole.
   */
  meterIds?: string
  resourceType?: string
  siteId?: Site["id"]
  sort?: string
  startDateFrom?: string
  startDateTo?: string
  /** Core API site-name and meter-code text filters, forwarded as published. */
  stringFilters?: BillTextFilterQueryOptions
}

export const buildBillsExportPath = (
  organizationId: Organization["id"],
  query: BillsExportQuery
): string | undefined => {
  if (!organizationId) {
    return undefined
  }

  const params = new URLSearchParams({
    format: query.format ?? "csv",
  })
  if (query.meterIds) {
    params.set("meter_ids", query.meterIds)
    if (query.siteId) {
      params.set("site_id", query.siteId)
    }
  } else if (query.meterId) {
    params.set("meter_id", query.meterId)
  } else if (query.siteId) {
    params.set("site_id", query.siteId)
  }
  if (query.resourceType) {
    params.set("resource_type", query.resourceType)
  }
  if (query.sort) {
    params.set("sort", query.sort)
  }
  if (query.startDateFrom) {
    params.set("start_date_from", query.startDateFrom)
  }
  if (query.startDateTo) {
    params.set("start_date_to", query.startDateTo)
  }
  appendStringFilters(params, query.stringFilters)

  return `/api/v1/organizations/${organizationId}/bills/export?${params.toString()}`
}

const appendStringFilters = (
  params: URLSearchParams,
  filters: BillTextFilterQueryOptions | undefined
) => {
  if (!filters) {
    return
  }

  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === "boolean") {
      params.set(key, String(value))
    } else if (typeof value === "string" && value.length > 0) {
      params.set(key, value)
    }
  }
}
